import { ChangeEvent, KeyboardEvent, forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import styles from "@shared/styles/components/ColorInput.module.css";
import { VerticalContainer } from "./Containers";
import designSystemStyles from "@shared/styles/designSystemStyles";
import { ErrorText, SmallText } from "./Texts";

export interface ColorInputRef {
    focus: () => void;
}

type ColorInputFormat = "hex" | "hex8" | "rgb";

interface ColorInputProps {
    label: string;
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    error?: string;
    loading?: boolean;
    width?: string;
    className?: string;
    disabled?: boolean;
    format?: ColorInputFormat;
    fallbackValue?: string;
}

const HEX_COLOR_REGEX = /^#([0-9a-f]{6}|[0-9a-f]{8})$/i;

const normalizeColorValue = (value: string, fallbackValue: string): string => {
    if (HEX_COLOR_REGEX.test(value)) {
        return value.toUpperCase();
    }

    if (HEX_COLOR_REGEX.test(fallbackValue)) {
        return fallbackValue.toUpperCase();
    }

    return "#000000";
};

const toColorPickerValue = (value: string, fallbackValue: string): string => {
    const normalized = normalizeColorValue(value, fallbackValue);
    return normalized.slice(0, 7);
};

export const ColorInput = forwardRef<ColorInputRef, ColorInputProps>(({
    label,
    value,
    onChange,
    placeholder,
    error,
    loading = false,
    width = "190px",
    className,
    disabled = false,
    format = "hex",
    fallbackValue = "#000000",
}, ref) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [draftValue, setDraftValue] = useState(value);

    useEffect(() => {
        setDraftValue(value);
    }, [value]);

    useImperativeHandle(ref, () => ({
        focus: () => {
            inputRef.current?.focus();
        }
    }));

    const commitValue = (nextValue: string) => {
        if (!nextValue) {
            onChange("");
            return;
        }

        if (HEX_COLOR_REGEX.test(nextValue)) {
            onChange(nextValue.toUpperCase());
            return;
        }

        onChange(normalizeColorValue(nextValue, fallbackValue));
    };

    const handleTextChange = (nextValue: string) => {
        setDraftValue(nextValue);
    };

    const handleTextBlur = () => {
        commitValue(draftValue.trim());
    };

    const handleTextKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
            event.preventDefault();
            commitValue(draftValue.trim());
        }
    };

    const handlePickerChange = (event: ChangeEvent<HTMLInputElement>) => {
        const nextValue = event.target.value.toUpperCase();
        setDraftValue(nextValue);
        onChange(nextValue);
    };

    return (
        <VerticalContainer
            gap={designSystemStyles.gapXxxSm}
            width={width || "100%"}
            className={["DSColorInput", className].filter(Boolean).join(" ")}
        >
            {label && (
                <SmallText text={label} />
            )}
            <div className={styles["field-row"]}>
                <div
                    className={[
                        styles["text-input-wrapper"],
                        error ? styles["text-input-wrapper-error"] : "",
                        loading ? styles["skeleton"] : "",
                    ].filter(Boolean).join(" ")}
                >
                    <input
                        ref={inputRef}
                        className={styles["text-input"]}
                        type="text"
                        placeholder={!loading ? placeholder : undefined}
                        value={draftValue}
                        onChange={(event) => handleTextChange(event.target.value)}
                        onBlur={handleTextBlur}
                        onKeyDown={handleTextKeyDown}
                        disabled={loading || disabled}
                        aria-label={label}
                    />
                </div>
                <label
                    className={[
                        styles["picker-trigger"],
                        loading ? styles["skeleton"] : "",
                        disabled ? styles["picker-trigger-disabled"] : "",
                    ].filter(Boolean).join(" ")}
                    aria-label={`${label} picker`}
                >
                    <span
                        className={styles["picker-swatch"]}
                        style={{ backgroundColor: toColorPickerValue(draftValue, fallbackValue) }}
                    />
                    <input
                        className={styles["native-picker"]}
                        type="color"
                        value={toColorPickerValue(draftValue, fallbackValue)}
                        onChange={handlePickerChange}
                        disabled={loading || disabled}
                        tabIndex={-1}
                    />
                </label>
            </div>
            {format !== "hex" && (
                <SmallText text={`Current format fallback: ${format.toUpperCase()} -> HEX`} color={designSystemStyles.colorTextSecondary} />
            )}
            {error && (
                <ErrorText text={error} />
            )}
        </VerticalContainer>
    );
});

ColorInput.displayName = "ColorInput";

export default ColorInput;
